import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import TemplateGallery from '../components/shared/templates/TemplateGallery';
import TemplateFieldsDialog from '../components/shared/templates/TemplateFieldsDialog';
import { templatesApi } from './templatesApi';

const LAST_PARENT_KEY = 'bb_template_last_parent';

/** Fired by NewFromTemplateButton when the user is already on the create page. */
export const APPLY_TEMPLATE_EVENT = 'bb:apply-template';

/** Remember the last parent chosen for a page template (per ref), best effort. */
export const readLastParentForTemplate = (ref) => {
    try {
        const map = JSON.parse(localStorage.getItem(LAST_PARENT_KEY) || '{}');
        return map[ref] || null;
    } catch (e) {
        return null;
    }
};

export const rememberParentForTemplate = (ref, parent) => {
    if (!ref || !parent) return;
    try {
        const map = JSON.parse(localStorage.getItem(LAST_PARENT_KEY) || '{}');
        map[ref] = parent;
        localStorage.setItem(LAST_PARENT_KEY, JSON.stringify(map));
    } catch (e) {
        /* convenience only */
    }
};

/**
 * One hook that drives "Start from a template" in the three create editors
 * (BBD-107): gallery → fields dialog → server render → prefill the editor,
 * or, for kits, instantiate and navigate to the new space.
 *
 * Options:
 *   workspaceId        – workspace context (null for personal documents)
 *   workspaceDomain    – for the post-kit navigation (/workspace/<domain>/space/<id>)
 *   authorUid          – for personal kit navigation (/documentation/Space/<name>/<uid>/)
 *   hasContent()       – true when the editor already has something typed (confirm before replacing)
 *   apply(rendered, template) – set name/description/content/type in the editor
 *   onKitCreated(result) – optional override for the kit navigation
 *   applyWorkspaceDefault – apply the workspace's default template on load (BBD-110);
 *                        "Start blank" (startBlank) is then one click away
 *   clear()            – reset name/description/content (used by startBlank)
 *   ready              – false while the page is still loading what the template needs
 *                        (workspace pages pass `Boolean(workspaceId)`); deep links and
 *                        the workspace default wait for it
 *
 * Returns { openGallery, applyTemplateRef, startBlank, defaultApplied, busy, error, element }.
 * Render `element` anywhere in the editor's JSX.
 *
 * Deep links: /create?template=preset:meeting-notes (and the workspace create
 * routes) open straight into the template's fields.
 */
export default function useTemplatePrefill({
    workspaceId = null,
    workspaceDomain = null,
    authorUid = null,
    hasContent = () => false,
    apply,
    onKitCreated,
    applyWorkspaceDefault = false,
    clear,
    ready = true,
    enabled = true,
} = {}) {
    const navigate = useNavigate();
    const location = useLocation();
    const [galleryOpen, setGalleryOpen] = useState(false);
    const [pending, setPending] = useState(null); // template awaiting field values
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const deepLinkHandled = useRef(null); // "<location.key>|<ref>" of the last deep link handled
    const defaultHandled = useRef(false);
    const mounted = useRef(true);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
    const [defaultApplied, setDefaultApplied] = useState(false);
    const applyRef = useRef(apply);
    useEffect(() => { applyRef.current = apply; }, [apply]);

    const finish = useCallback(async (template, values) => {
        setBusy(true);
        setError('');
        try {
            if (template.kind === 'kit') {
                const result = await templatesApi.instantiate({ ref: template.ref, values, workspaceId });
                setPending(null);
                if (onKitCreated) {
                    onKitCreated(result);
                } else if (result.workspace_id && workspaceDomain) {
                    navigate(`/workspace/${workspaceDomain}/space/${result.space.id}`);
                } else if (!result.workspace_id && (authorUid || result.space.author)) {
                    navigate(`/documentation/Space/${encodeURIComponent(result.space.name)}/${authorUid || result.space.author}/`);
                } else {
                    navigate('/');
                }
                return;
            }
            const rendered = await templatesApi.render({ ref: template.ref, values, workspaceId });
            setPending(null);
            applyRef.current?.(rendered, template);
        } catch (e) {
            if (e.status === 413 && e.data?.storage_info) {
                setError(e.data.message || 'Storage limit exceeded. Free up space or upgrade your plan.');
            } else {
                setError(e.message || 'Could not apply the template');
            }
        } finally {
            setBusy(false);
        }
    }, [workspaceId, workspaceDomain, authorUid, navigate, onKitCreated]);

    const startTemplate = useCallback((template) => {
        setGalleryOpen(false);
        setError('');
        if (template.kind !== 'kit' && hasContent()) {
            const ok = window.confirm('Replace what you have typed so far with this template?');
            if (!ok) return;
        }
        if (template.fields && template.fields.length) {
            setPending(template);
        } else {
            finish(template, {});
        }
    }, [finish, hasContent]);

    // The editors pass fresh `hasContent` / `apply` closures on every render,
    // so `startTemplate` changes identity constantly. Effects below must not
    // depend on it: a re-render would cancel their in-flight request while the
    // "handled" flag stops a retry, and the template would silently never
    // apply. They read the latest callbacks through refs instead.
    const startTemplateRef = useRef(startTemplate);
    const hasContentRef = useRef(hasContent);
    useEffect(() => { startTemplateRef.current = startTemplate; hasContentRef.current = hasContent; });

    // Deep link: ?template=<ref> (sidebar / dashboard "Templates", My Templates
    // "Use", Quick Links). Keyed on the navigation so picking another template
    // later, or the same one again, is handled too.
    const deepLinkRef = new URLSearchParams(location.search).get('template');
    useEffect(() => {
        if (!enabled || !ready || !deepLinkRef) return;
        const handledKey = `${location.key}|${deepLinkRef}`;
        if (deepLinkHandled.current === handledKey) return;
        deepLinkHandled.current = handledKey;
        const ref = deepLinkRef;
        // The gallery payload carries the template's fields; fall back to a
        // preview render for refs the gallery does not list (hidden presets).
        templatesApi.gallery({ workspaceId, includeContent: false })
            .then(async (g) => {
                const all = [...(g.recent || []), ...(g.workspace || []), ...(g.personal || []), ...(g.presets || [])];
                let template = all.find((t) => t.ref === ref);
                if (!template) {
                    const r = await templatesApi.render({ ref, workspaceId, preview: true });
                    template = { ref, kind: r.kind, name: r.name, fields: [] };
                }
                if (mounted.current) startTemplateRef.current(template);
            })
            .catch((e) => { if (mounted.current) setError(e.message || 'That template link is not valid'); });
    }, [enabled, ready, deepLinkRef, location.key, workspaceId]);

    // Same-page choice: the sidebar's Templates button on a create page hands
    // the template over directly instead of navigating to the page we are on.
    useEffect(() => {
        if (!enabled) return undefined;
        const onApply = (e) => {
            if (e.detail?.template) startTemplateRef.current(e.detail.template);
        };
        window.addEventListener(APPLY_TEMPLATE_EVENT, onApply);
        return () => window.removeEventListener(APPLY_TEMPLATE_EVENT, onApply);
    }, [enabled]);

    // Workspace default template (BBD-110): applied once, only on an empty
    // editor and only when no deep link asked for something else. A missing
    // or deleted default is simply null (the backend never errors).
    useEffect(() => {
        if (!enabled || !ready || !applyWorkspaceDefault || !workspaceId || defaultHandled.current) return;
        if (deepLinkRef) return;
        defaultHandled.current = true;
        templatesApi.gallery({ workspaceId, includeContent: false })
            .then((g) => {
                if (!mounted.current || !g.default_template || hasContentRef.current()) return;
                setDefaultApplied(true);
                startTemplateRef.current(g.default_template);
            })
            .catch(() => { /* blank editor is the fallback */ });
    }, [enabled, ready, applyWorkspaceDefault, workspaceId, deepLinkRef]);

    const startBlank = useCallback(() => {
        setPending(null);
        setDefaultApplied(false);
        clear?.();
    }, [clear]);

    const openGallery = useCallback(() => { setError(''); setGalleryOpen(true); }, []);

    const element = enabled ? (
        <>
            <TemplateGallery
                isOpen={galleryOpen}
                onClose={() => setGalleryOpen(false)}
                onChoose={startTemplate}
                workspaceId={workspaceId}
            />
            {pending && (
                <TemplateFieldsDialog
                    template={pending}
                    workspaceId={workspaceId}
                    submitLabel={pending.kind === 'kit' ? 'Create pages' : 'Apply template'}
                    busy={busy}
                    error={error}
                    onSubmit={(values) => finish(pending, values)}
                    onCancel={() => { if (!busy) { setPending(null); setError(''); } }}
                />
            )}
        </>
    ) : null;

    return { openGallery, applyTemplateRef: startTemplate, startBlank, defaultApplied, busy, error, element };
}
